import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Loader2, X } from "lucide-react";

// Peças visuais da "Minha conta". A cor de destaque vem sempre de `primary`
// (tema ou cor escolhidos pelo cliente); nada de azul fixo aqui.

export const cx = (...classes) => classes.filter(Boolean).join(' ');

export function Section({ id, title, description, aside, children, footer, className }) {
    return (
        <section id={id} className={cx('rounded-xl border border-[#ececf0] bg-white', className)}>
            {(title || aside) && (
                <header className="flex flex-col gap-3 px-6 pt-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                        {title && <h2 className="m-0 text-[15px] font-semibold text-[#1d1d1f]">{title}</h2>}
                        {description && <p className="m-0 mt-1 max-w-2xl text-[13px] leading-5 text-[#6b6b76]">{description}</p>}
                    </div>
                    {aside && <div className="shrink-0">{aside}</div>}
                </header>
            )}

            {children ? <div className="px-6 py-5">{children}</div> : <div className="pb-5" />}

            {footer && (
                <footer className="flex flex-wrap items-center justify-end gap-3 rounded-b-xl border-t border-[#ececf0] bg-[#fafafb] px-6 py-3">
                    {footer}
                </footer>
            )}
        </section>
    );
}

export function FieldGrid({ children, columns = 2 }) {
    return (
        <div className={cx('grid grid-cols-1 gap-x-5 gap-y-4', columns === 2 && 'sm:grid-cols-2', columns === 3 && 'sm:grid-cols-3')}>
            {children}
        </div>
    );
}

export function Field({ label, htmlFor, hint, error, required, className, children }) {
    return (
        <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
            {label && (
                <label htmlFor={htmlFor} className="text-[13px] font-medium text-[#3a3a44]">
                    {label}
                    {required && <span className="ml-0.5 text-[#c1341a]" aria-hidden="true">*</span>}
                </label>
            )}
            {children}
            {error ? (
                <span id={htmlFor ? `${htmlFor}-error` : undefined} className="text-xs text-[#c1341a]">{error}</span>
            ) : hint ? (
                <span className="text-xs text-[#8a8a94]">{hint}</span>
            ) : null}
        </div>
    );
}

const controlClass = (invalid, disabled) => cx(
    'h-10 w-full rounded-lg border bg-white px-3 text-sm text-[#1d1d1f] outline-none transition',
    'placeholder:text-[#a5a5ae] focus:border-primary focus:ring-4 focus:ring-[color-mix(in_srgb,var(--color-primary)_14%,transparent)]',
    invalid ? 'border-[#c1341a]' : 'border-[#dcdce2] hover:border-[#c4c4cc]',
    disabled && 'cursor-not-allowed bg-[#f5f5f7] text-[#8a8a94]',
);

export function TextInput({ id, value, onChange, invalid, disabled, multiline, rows = 3, className, ...props }) {
    const shared = {
        id,
        value: value ?? '',
        disabled,
        'aria-invalid': invalid || undefined,
        'aria-describedby': invalid && id ? `${id}-error` : undefined,
        onChange: (event) => onChange?.(event.target.value, event),
        ...props,
    };

    if (multiline)
        return <textarea rows={rows} className={cx(controlClass(invalid, disabled), 'h-auto py-2.5 leading-5', className)} {...shared} />;

    return <input className={cx(controlClass(invalid, disabled), className)} {...shared} />;
}

export function SelectInput({ id, value, onChange, options = [], placeholder = 'Selecione', invalid, disabled, className }) {
    return (
        <select
            id={id}
            value={value ?? ''}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className={cx(controlClass(invalid, disabled), 'pr-9', className)}
            onChange={(event) => onChange?.(event.target.value, event)}
        >
            <option value="" disabled>{placeholder}</option>
            {options.map(option => (
                <option key={option.value} value={option.value}>{option.name}</option>
            ))}
        </select>
    );
}

const BUTTON_VARIANTS = {
    primary: 'bg-primary text-white shadow-sm hover:brightness-110 active:brightness-95',
    secondary: 'border border-[#dcdce2] bg-white text-[#1d1d1f] hover:border-[#c4c4cc] hover:bg-[#fafafb]',
    ghost: 'text-[#3a3a44] hover:bg-[#f2f2f5]',
    danger: 'border border-[#f1d2cc] bg-white text-[#c1341a] hover:bg-[#fdf3f1]',
    link: 'px-0 text-primary hover:underline',
};

export function ActionButton({ variant = 'primary', size = 'md', icon: Icon, loading, disabled, className, children, type = 'button', ...props }) {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={cx(
                'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[color-mix(in_srgb,var(--color-primary)_22%,transparent)]',
                size === 'sm' ? 'h-8 px-3 text-[13px]' : 'h-10 px-4 text-sm',
                BUTTON_VARIANTS[variant],
                (disabled || loading) && 'pointer-events-none opacity-50',
                className,
            )}
            {...props}
        >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
            {children}
        </button>
    );
}

const PILL_TONES = {
    success: 'bg-[#e8f5ee] text-[#1f7a4f]',
    warning: 'bg-[#fdf1e3] text-[#9a5512]',
    danger: 'bg-[#fdecea] text-[#b3261e]',
    neutral: 'bg-[#f2f2f5] text-[#55555f]',
    brand: 'bg-primaryTint text-primary',
};

export function StatusPill({ tone = 'neutral', icon: Icon, children, className }) {
    return (
        <span className={cx('inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium', PILL_TONES[tone], className)}>
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
            {children}
        </span>
    );
}

export function IconTile({ icon: Icon, tone = 'brand', className }) {
    const tones = {
        brand: 'bg-primaryTint text-primary',
        success: 'bg-[#e8f5ee] text-[#1f7a4f]',
        warning: 'bg-[#fdf1e3] text-[#9a5512]',
        danger: 'bg-[#fdecea] text-[#b3261e]',
        neutral: 'bg-[#f2f2f5] text-[#55555f]',
    };

    return (
        <span className={cx('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tones[tone], className)}>
            <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
    );
}

export function Notice({ tone = 'neutral', icon: Icon, title, children, action, className }) {
    const tones = {
        neutral: 'border-[#e6e6ea] bg-[#fafafb] text-[#3a3a44]',
        brand: 'border-[color-mix(in_srgb,var(--color-primary)_25%,#fff)] bg-primaryTint text-[#2a2a33]',
        warning: 'border-[#f5dcbc] bg-[#fef8f0] text-[#6b3d0c]',
        danger: 'border-[#f3cfc9] bg-[#fdf3f1] text-[#7a1f16]',
        success: 'border-[#cfe9db] bg-[#f1faf5] text-[#17553a]',
    };

    return (
        <div role={tone === 'danger' ? 'alert' : undefined} className={cx('flex gap-3 rounded-lg border px-4 py-3 text-[13px] leading-5', tones[tone], className)}>
            {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
            <div className="min-w-0 flex-1">
                {title && <p className="m-0 font-semibold">{title}</p>}
                {children && <div className={cx(title && 'mt-0.5')}>{children}</div>}
            </div>
            {action && <div className="shrink-0 self-center">{action}</div>}
        </div>
    );
}

export function Skeleton({ className }) {
    return <div className={cx('animate-pulse rounded-md bg-[#efeff3]', className)} />;
}

export function SectionSkeleton() {
    return (
        <div className="rounded-xl border border-[#ececf0] bg-white p-6" aria-busy="true" aria-label="Carregando">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-2 h-3 w-72" />
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
            </div>
        </div>
    );
}

export function Toggle({ id, checked, onChange, disabled, label }) {
    return (
        <button
            id={id}
            type="button"
            role="switch"
            aria-checked={!!checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => onChange?.(!checked)}
            className={cx(
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition',
                checked ? 'bg-primary' : 'bg-[#d6d6dd]',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <span className={cx('inline-block h-5 w-5 rounded-full bg-white shadow transition', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
        </button>
    );
}

export const initialsOf = (name = '') =>
    String(name)
        .replace(/\([^)]*\)/g, ' ')
        .replace(/[^\p{L}\s]/gu, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .filter((part, index, parts) => index === 0 || index === parts.length - 1)
        .map(part => part[0])
        .join('')
        .toUpperCase() || '?';

export function Avatar({ name, imageUrl, size = 'md' }) {
    const sizes = { sm: 'h-8 w-8 text-xs', md: 'h-10 w-10 text-sm', lg: 'h-14 w-14 text-lg' };

    if (imageUrl)
        return <img src={imageUrl} alt="" className={cx('shrink-0 rounded-full object-cover', sizes[size])} />;

    return (
        <span className={cx('flex shrink-0 items-center justify-center rounded-full bg-primaryTint font-semibold text-primary', sizes[size])} aria-hidden="true">
            {initialsOf(name)}
        </span>
    );
}

export function Modal({ open, onClose, title, description, icon: Icon, children, footer, size = 'md', dismissible = true }) {
    const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl' };

    return (
        <Transition show={!!open} as={Fragment}>
            <Dialog as="div" className="relative z-[1400]" onClose={dismissible ? onClose : () => { }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-[#101018]/40 backdrop-blur-[2px]" aria-hidden="true" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200" enterFrom="opacity-0 translate-y-3 sm:scale-[.98]" enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
                        >
                            <Dialog.Panel className={cx('w-full overflow-hidden rounded-t-2xl bg-white text-[#1d1d1f] shadow-[0_24px_64px_rgba(16,16,24,0.24)] sm:rounded-2xl', widths[size])}>
                                <div className="flex items-start gap-3 px-6 pt-5">
                                    {Icon && <IconTile icon={Icon} />}
                                    <div className="min-w-0 flex-1">
                                        <Dialog.Title className="m-0 text-base font-semibold">{title}</Dialog.Title>
                                        {description && <Dialog.Description className="m-0 mt-1 text-[13px] leading-5 text-[#6b6b76]">{description}</Dialog.Description>}
                                    </div>
                                    {dismissible && (
                                        <button type="button" onClick={onClose} aria-label="Fechar" className="-mr-2 rounded-lg p-1.5 text-[#8a8a94] transition hover:bg-[#f2f2f5] hover:text-[#1d1d1f]">
                                            <X className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    )}
                                </div>

                                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

                                {footer && (
                                    <div className="flex flex-col-reverse gap-2 border-t border-[#ececf0] bg-[#fafafb] px-6 py-3 sm:flex-row sm:items-center sm:justify-end">
                                        {footer}
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
